import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Download, Loader2, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fetchPartiesReport, PartyReportItem } from '@/app/services/supabaseEdgeFunctionService';
import { Badge } from '@/components/ui/badge';
import { useQuery } from '@tanstack/react-query';
import { MultiSelect, MultiSelectOption } from '@/app/components/common/MultiSelect';
import { listSites } from '@/app/services/siteService';
import { listParties, listPartyGroups } from '@/app/services/partyService';

const PartiesReportPage: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<PartyReportItem[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState<PartyReportItem[]>([]);
    const [selectedSiteIds, setSelectedSiteIds] = useState<number[]>([]);
    const [selectedPartyIds, setSelectedPartyIds] = useState<number[]>([]);
    const [selectedPartyGroupIds, setSelectedPartyGroupIds] = useState<number[]>([]);
    const [selectedResponsiblePartyIds, setSelectedResponsiblePartyIds] = useState<number[]>([]);

    // Fetch sites for dropdown
    const { data: sitesData = { items: [] }, isLoading: isLoadingSites } = useQuery({
        queryKey: ['sites-for-parties-report'],
        queryFn: () => listSites({ limit: 100 }),
        staleTime: 5 * 60 * 1000,
    });

    // Fetch parties for dropdown
    const { data: partiesData = { items: [] }, isLoading: isLoadingParties } = useQuery({
        queryKey: ['parties-for-parties-report'],
        queryFn: () => listParties({ limit: 500, filters: JSON.stringify([{ field: "is_active", op: "eq", value: true }]) }),
        staleTime: 5 * 60 * 1000,
    });

    // Fetch party groups for dropdown
    const { data: partyGroupsData = { items: [] }, isLoading: isLoadingPartyGroups } = useQuery({
        queryKey: ['party-groups-for-parties-report'],
        queryFn: () => listPartyGroups({ limit: 100 }),
        staleTime: 5 * 60 * 1000,
    });

    // Fetch EMPLOYEE party group to get its ID
    const { data: employeeGroupData } = useQuery({
        queryKey: ['employee-party-group'],
        queryFn: () => listPartyGroups({
            filters: JSON.stringify([{ field: "name", op: "eq", value: "EMPLOYEE" }]),
            limit: 1
        }),
        staleTime: 15 * 60 * 1000,
    });

    const employeeGroupId = employeeGroupData?.items?.[0]?.id;

    // Fetch EMPLOYEE parties for responsible party dropdown
    const { data: responsiblePartiesData = { items: [] }, isLoading: isLoadingResponsibleParties } = useQuery({
        queryKey: ['responsible-parties-for-parties-report', employeeGroupId],
        queryFn: () => listParties({
            limit: 500,
            filters: JSON.stringify([
                { field: "party_group_id", op: "eq", value: employeeGroupId },
                { field: "is_active", op: "eq", value: true }
            ])
        }),
        enabled: !!employeeGroupId,
        staleTime: 5 * 60 * 1000,
    });

    // Transform data for multi-select components
    const siteOptions: MultiSelectOption[] = sitesData.items?.map(site => ({
        id: site.id,
        name: site.name,
        extra: (site as any).code
    })) || [];

    const partyOptions: MultiSelectOption[] = partiesData.items?.map(party => ({
        id: party.id,
        name: party.name,
        extra: party.phone || (party as any).party_code
    })) || [];

    const partyGroupOptions: MultiSelectOption[] = partyGroupsData.items?.map(group => ({
        id: group.id,
        name: group.name
    })) || [];

    const responsiblePartyOptions: MultiSelectOption[] = responsiblePartiesData.items?.map(party => ({
        id: party.id,
        name: party.name,
        extra: party.phone || (party as any).party_code
    })) || [];

    const fetchReport = async () => {
        setLoading(true);
        try {
            const response = await fetchPartiesReport({
                site_ids: selectedSiteIds.length > 0 ? selectedSiteIds : undefined,
                party_ids: selectedPartyIds.length > 0 ? selectedPartyIds : undefined,
                party_group_ids: selectedPartyGroupIds.length > 0 ? selectedPartyGroupIds : undefined,
                responsible_party_ids: selectedResponsiblePartyIds.length > 0 ? selectedResponsiblePartyIds : undefined,
            });
            setData(response);
            setFilteredData(response);
            if (response.length > 0) {
                toast.success(`Loaded ${response.length} parties`);
            } else {
                toast.info('No parties found');
            }
        } catch (error: any) {
            toast.error(error.message || 'Failed to fetch report. Please try again.');
            setData([]);
            setFilteredData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, [selectedSiteIds, selectedPartyIds, selectedPartyGroupIds, selectedResponsiblePartyIds]);

    // Filter data based on search term
    useEffect(() => {
        if (searchTerm) {
            const filtered = data.filter(party => 
                party.party_name.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredData(filtered);
        } else {
            setFilteredData(data);
        }
    }, [searchTerm, data]);

    const handleExport = () => {
        // Create comprehensive CSV data
        const csvRows = [];

        // Add header with filter information
        csvRows.push(['Party Statement Report']);
        csvRows.push([`Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}`]);
        csvRows.push(['']);

        // Add filter information
        if (selectedSiteIds.length > 0) {
            const selectedSites = sitesData.items?.filter(s => selectedSiteIds.includes(s.id)).map(s => s.name).join(', ');
            csvRows.push([`Sites: ${selectedSites}`]);
        }
        if (selectedPartyIds.length > 0) {
            csvRows.push([`Selected ${selectedPartyIds.length} parties`]);
        }
        if (selectedPartyGroupIds.length > 0) {
            const selectedGroups = partyGroupsData.items?.filter(g => selectedPartyGroupIds.includes(g.id)).map(g => g.name).join(', ');
            csvRows.push([`Party Groups: ${selectedGroups}`]);
        }
        if (selectedResponsiblePartyIds.length > 0) {
            const selectedResponsibleParties = responsiblePartiesData.items?.filter(p => selectedResponsiblePartyIds.includes(p.id)).map(p => p.name).join(', ');
            csvRows.push([`Responsible Parties: ${selectedResponsibleParties}`]);
        }
        if (searchTerm) {
            csvRows.push([`Search Term: ${searchTerm}`]);
        }
        csvRows.push(['']);

        // Add data headers
        const headers = [
            'Party Name',
            'Sales Invoices',
            'Receipts',
            'Closing Balance',
            'Balance Type',
            'Unallocated Credit'
        ];
        csvRows.push(headers);

        // Add data rows
        filteredData.forEach(row => {
            csvRows.push([
                `"${row.party_name}"`,
                row.sales_invoices_count,
                row.receipts_count,
                row.closing_balance,
                row.closing_balance_type,
                row.unallocated_credit
            ]);
        });

        // Convert to CSV string
        const csvContent = csvRows.map(row => row.join(',')).join('\n');

        // Download CSV
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `parties-statement-${format(new Date(), 'yyyy-MM-dd')}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Report exported successfully');
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(value);
    };

    return (
        <div className="container mx-auto py-6 space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>Party Statement Summary</CardTitle>
                    <CardDescription>
                        Overview of all parties with their outstanding balances and transaction counts
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {/* Summary Cards */}
                    {data.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                                <div className="text-sm text-red-700 mb-1">Total Receivables (Dr)</div>
                                <div className="text-2xl font-semibold text-red-800">
                                    {formatCurrency(
                                        data
                                            .filter(p => p.closing_balance_type === 'Dr')
                                            .reduce((sum, p) => sum + p.closing_balance, 0)
                                    )}
                                </div>
                                <div className="text-xs text-red-600 mt-1">
                                    {data.filter(p => p.closing_balance_type === 'Dr').length} parties owe you money
                                </div>
                            </div>
                            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                <div className="text-sm text-green-700 mb-1">Total Payables (Cr)</div>
                                <div className="text-2xl font-semibold text-green-800">
                                    {formatCurrency(
                                        data
                                            .filter(p => p.closing_balance_type === 'Cr')
                                            .reduce((sum, p) => sum + p.closing_balance, 0)
                                    )}
                                </div>
                                <div className="text-xs text-green-600 mt-1">
                                    You owe {data.filter(p => p.closing_balance_type === 'Cr').length} parties
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Filters Section */}
                    <div className="space-y-4 mb-6">
                        {/* Multi-Select Filters */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="space-y-2">
                                <Label>Sites</Label>
                                <MultiSelect
                                    options={siteOptions}
                                    value={selectedSiteIds}
                                    onChange={setSelectedSiteIds}
                                    placeholder="All sites"
                                    searchPlaceholder="Search sites..."
                                    loading={isLoadingSites}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Parties</Label>
                                <MultiSelect
                                    options={partyOptions}
                                    value={selectedPartyIds}
                                    onChange={setSelectedPartyIds}
                                    placeholder="All parties"
                                    searchPlaceholder="Search parties..."
                                    loading={isLoadingParties}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Party Groups</Label>
                                <MultiSelect
                                    options={partyGroupOptions}
                                    value={selectedPartyGroupIds}
                                    onChange={setSelectedPartyGroupIds}
                                    placeholder="All party groups"
                                    searchPlaceholder="Search groups..."
                                    loading={isLoadingPartyGroups}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Responsible Parties</Label>
                                <MultiSelect
                                    options={responsiblePartyOptions}
                                    value={selectedResponsiblePartyIds}
                                    onChange={setSelectedResponsiblePartyIds}
                                    placeholder="All employees"
                                    searchPlaceholder="Search employees..."
                                    loading={isLoadingResponsibleParties}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Search and Actions */}
                    <div className="flex flex-col sm:flex-row gap-4 mb-6">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search parties..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={fetchReport} disabled={loading}>
                                {loading ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                )}
                                Refresh
                            </Button>
                            <Button onClick={handleExport} variant="outline" disabled={loading || filteredData.length === 0}>
                                <Download className="mr-2 h-4 w-4" />
                                Export
                            </Button>
                        </div>
                    </div>

                    {/* Report Table */}
                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Party Name</TableHead>
                                    <TableHead className="text-center">Sales Invoices</TableHead>
                                    <TableHead className="text-center">Receipts</TableHead>
                                    <TableHead className="text-right">Outstanding Balance</TableHead>
                                    <TableHead className="text-right">Unallocated Credit</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-8">
                                            <Loader2 className="h-8 w-8 animate-spin mx-auto" />
                                            <p className="mt-2 text-muted-foreground">Loading party statements...</p>
                                        </TableCell>
                                    </TableRow>
                                ) : filteredData.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                            {searchTerm ? 'No parties found matching your search' : 'No parties found'}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredData.map((party) => (
                                        <TableRow key={party.party_id}>
                                            <TableCell className="font-medium">
                                                <a 
                                                    href={`/party/detail/${party.party_id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                                                >
                                                    {party.party_name}
                                                </a>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <Badge variant="secondary">{party.sales_invoices_count}</Badge>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <Badge variant="secondary">{party.receipts_count}</Badge>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <span className={`font-medium ${
                                                        party.closing_balance_type === 'Dr' 
                                                            ? 'text-red-600' 
                                                            : 'text-green-600'
                                                    }`}>
                                                        {formatCurrency(party.closing_balance)}
                                                    </span>
                                                    <Badge 
                                                        variant={party.closing_balance_type === 'Dr' ? 'destructive' : 'default'}
                                                        className="text-xs"
                                                    >
                                                        {party.closing_balance_type}
                                                    </Badge>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {party.unallocated_credit > 0 ? (
                                                    <span className="text-blue-600 font-medium">
                                                        {formatCurrency(party.unallocated_credit)}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted-foreground">-</span>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Footer */}
                    {filteredData.length > 0 && searchTerm && (
                        <div className="mt-4">
                            <div className="text-sm text-muted-foreground text-center">
                                Showing {filteredData.length} of {data.length} parties
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default PartiesReportPage;